import { forwardRef, useImperativeHandle, useRef } from 'react';
import { authedFetch } from '../../ai/authedFetch';
import { fetchPollStatus } from '../../utils/pollRetry';
import { capabilityDeniedMessage } from '../../utils/capabilityErrors';
import { readSubmitGrant, sumGrants } from '../../utils/runGrant';
import { FM4M_MAX_SAMPLES_PER_JOB, splitJobs } from '../../utils/generationBounds';
// Part 1a: every job body comes from the row's payload (src/ai/rows/rowPayloads.js).
import { fm4mGenerationJobBody, fm4mFeaturizeBody } from '../../ai/rows/rowPayloads';

const POLL_INTERVAL_MS = 1000;

/**
 * Submit a job (generation via POST /jobs, or representation via POST
 * /jobs/representation, D3 durability fix, 2026-08-20: the /representation
 * featurization loop used to be a plain synchronous fetch per chunk with no
 * job id at all, so a tab close mid-chunk lost that chunk's candidate rows
 * permanently even if compute finished server-side, see
 * docs/DURABILITY-AUDIT-2026-08-20.md finding D3), poll until done, return
 * the result. Both submit paths land in the same JOBS/JOB_QUEUE/worker
 * machinery server-side (models/ibm-materials-service/main.py), so this one
 * poll loop already works for either: only submitPath and the label used
 * in onStatus differ. Mirrors MatterGen's async job pattern, avoids Cloud
 * Run cold-start timeouts.
 */
async function submitAndPoll({ base, submitPath = '/jobs', body, signal, activeJobIds, onStatus, statusLabel = 'FM4M: generating', authHeaders, onGrant }) {
  if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');

  const hdrs = { 'Content-Type': 'application/json', ...authHeaders };

  // Submit job — returns immediately with job_id
  const submitResp = await authedFetch(`${base}${submitPath}`, {
    method: 'POST',
    headers: hdrs,
    body: JSON.stringify(body),
    signal,
  });
  if (!submitResp.ok) {
    let j = null;
    try { j = await submitResp.json(); } catch (_) {}
    const denied = capabilityDeniedMessage(j);
    if (denied) throw new Error(denied);
    let msg = `FM4M job submit failed: ${submitResp.status}`;
    if (j?.detail) msg += `: ${typeof j.detail === 'string' ? j.detail : JSON.stringify(j.detail)}`;
    throw new Error(msg);
  }
  const submitted = await submitResp.json();
  const { job_id } = submitted;
  // 0.3: a generation submit reports what the plan's per-run limit granted (the submit
  // JSON, or llm-proxy's X-Spectra-* headers). Representation submits pass no onGrant.
  if (typeof onGrant === 'function') onGrant(readSubmitGrant(submitResp, submitted));
  activeJobIds.current.push({ id: job_id, base, authHeaders });

  const onAbort = () => {
    authedFetch(`${base}/jobs/${job_id}`, { method: 'DELETE', headers: authHeaders }).catch(() => {});
  };
  signal?.addEventListener('abort', onAbort);

  try {
    // Poll until done
    while (true) {
      if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
      await new Promise(r => setTimeout(r, POLL_INTERVAL_MS));
      if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');

      const statusResp = await fetchPollStatus(() => authedFetch(`${base}/jobs/${job_id}`, { headers: authHeaders, signal }), { signal });
      if (!statusResp.ok) throw new Error(`FM4M job poll failed: ${statusResp.status}`);
      const status = await statusResp.json();

      if (onStatus && (status.status === 'running' || status.status === 'queued')) {
        const elapsed = status.elapsed_sec;
        const mins = elapsed ? Math.floor(elapsed / 60) : 0;
        const secs = elapsed ? Math.floor(elapsed % 60) : 0;
        const timeStr = elapsed ? ` — ${mins}m ${String(secs).padStart(2, '0')}s` : '';
        const queueNote = status.status === 'queued' ? ' (queued)' : '';
        const countNote = status.num_samples != null ? ` ${status.num_samples} molecules`
          : (status.num_inputs != null ? ` ${status.num_inputs} inputs` : '');
        onStatus(`${statusLabel}${countNote}${timeStr}${queueNote}`);
      }

      if (status.status === 'succeeded') {
        const resultResp = await authedFetch(`${base}/jobs/${job_id}/result`, { headers: authHeaders, signal });
        if (!resultResp.ok) throw new Error(`FM4M result fetch failed: ${resultResp.status}`);
        return await resultResp.json();
      }
      if (status.status === 'cancelled') {
        throw new DOMException('Job cancelled', 'AbortError');
      }
      if (status.status === 'failed') {
        const resultResp = await authedFetch(`${base}/jobs/${job_id}/result`, { headers: authHeaders }).catch(() => null);
        let msg = 'FM4M generation failed';
        if (resultResp) {
          try {
            const err = await resultResp.json();
            const d = err?.detail || err;
            if (typeof d === 'string') msg += ` — ${d}`;
            else if (d?.error || d?.message) msg += ` — ${[d.error, d.message].filter(Boolean).join(': ')}`;
          } catch (_) {}
        }
        throw new Error(msg);
      }
    }
  } finally {
    signal?.removeEventListener('abort', onAbort);
    activeJobIds.current = activeJobIds.current.filter(j => j.id !== job_id);
  }
}

/**
 * Seedless polymer/molecule generation for one FM4M generate row, via FM4M's async job
 * endpoint. Generation is ALWAYS attempted here, so any failure is real and must propagate
 * (I7a). Previously a non-abort failure was caught and discarded, falling through to
 * `{ skipped: true }`, so the run showed no FM4M rows, no error, and was marked completed.
 * Returns the resulting SMILES (possibly empty: a successful call that legitimately
 * generated nothing is NOT an error; the caller handles that case).
 *
 * 0.4 (spec 2026-10-07): the service rejects num_samples above 1000 with 422, so a larger
 * request runs as several jobs of at most FM4M_MAX_SAMPLES_PER_JOB, one after another, and
 * their SMILES are concatenated in job order. A failed job keeps the other jobs' molecules;
 * only when every job fails is the first error rethrown (I7a).
 *
 * Part 1a (contract 1.6): `payload` is fm4mPayloadForRow(row). Every job body is
 * fm4mGenerationJobBody(payload, n), which states the generator, num_samples, the oversample
 * (1 without filters, so the server's per-run tally charges each job
 * min(num_samples x oversample, 1000), the per-job charge chargeOf sums, R4-2), max_length
 * and filters ({} when unfiltered) explicitly. The generator is the row target, which row
 * validation limits to 'bart' (M2: 'mhg' and 'smi-ted' made every job fail).
 */
export async function generateSeedSmiles({ base, payload, signal, activeJobIds, onStatus, authHeaders, onGrant }) {
  const p = payload || {};
  const generation = p.generation || {};
  const hasFilters = !!generation.filters && Object.keys(generation.filters).length > 0;
  if (onStatus) onStatus(hasFilters ? 'Generating & filtering molecules with FM4M...' : 'Generating polymers with FM4M...');
  const jobSizes = splitJobs(p.candidates, FM4M_MAX_SAMPLES_PER_JOB);
  const smiles = [];
  let generated = 0;
  let passed = 0;
  let sawStats = false;
  let succeeded = 0;
  let firstError = null;
  for (let j = 0; j < jobSizes.length; j += 1) {
    const genBody = fm4mGenerationJobBody(p, jobSizes[j]);
    const statusLabel = jobSizes.length > 1 ? `FM4M: generating (job ${j + 1} of ${jobSizes.length})` : 'FM4M: generating';
    try {
      // 0.3: each accepted job's grant goes to onGrant; execute sums them onto the row's first run.
      const genData = await submitAndPoll({ base, body: genBody, signal, activeJobIds, onStatus, statusLabel, authHeaders, onGrant });
      succeeded += 1;
      if (Array.isArray(genData.smiles)) smiles.push(...genData.smiles.filter((s) => typeof s === 'string' && s.trim()));
      if (genData.stats) {
        sawStats = true;
        generated += Number(genData.stats.generated) || 0;
        passed += Number(genData.stats.passed_filter) || 0;
      }
    } catch (e) {
      if (e?.name === 'AbortError') throw e;
      if (!firstError) firstError = e;
      console.warn(`FM4M: generation job ${j + 1} of ${jobSizes.length} failed; continuing with the rest.`, e);
    }
  }
  if (succeeded === 0) throw firstError || new Error('FM4M generation failed: nothing to generate');
  const failed = jobSizes.length - succeeded;
  if (failed > 0 && onStatus) onStatus(`FM4M: ${failed} of ${jobSizes.length} generation jobs failed; kept the molecules from the rest`);
  else if (sawStats && onStatus) onStatus(`FM4M: generated ${generated}, ${passed} passed filters`);
  return smiles;
}

const FM4MRunner = forwardRef(function FM4MRunner({ onStatus, baseUrl, authHeaders }, ref) {
  const activeJobIds = useRef([]);

  useImperativeHandle(ref, () => ({
    // Part 1a (contract 1.6): `payload` is fm4mPayloadForRow(row) (src/ai/rows/rowPayloads.js).
    // A generate row generates payload.candidates molecules with its own generation settings
    // and featurizes them; a represent row featurizes its seeds and never generates. The
    // runner reads nothing else. opts = { onProgress, signal, baseUrl, authHeaders }.
    async execute(payload, opts) {
      const options = opts || {};
      const p = payload || {};
      const featurize = p.featurize || {};
      const isRepresent = p.kind === 'represent';
      const label = p.domain === 'polymer' ? 'polymer' : 'small_molecule';

      const base = options.baseUrl || baseUrl || process.env.REACT_APP_IBM_API || 'http://localhost:8011';
      const effAuthHeaders = options.authHeaders !== undefined ? options.authHeaders : authHeaders;

      // A generate row ALWAYS attempts generation: any failure below is real and must
      // propagate (I7a), not be swallowed into a silent `{ skipped: true }` that hid the run's
      // failure from the user.
      // 0.3: the grant of every generation job this row submits (none for a represent row).
      const generationGrants = [];
      let smiles = isRepresent && Array.isArray(p.seeds) ? [...p.seeds] : [];
      if (!isRepresent) {
        smiles = await generateSeedSmiles({
          base,
          payload: p,
          signal: options.signal,
          activeJobIds,
          onStatus,
          authHeaders: effAuthHeaders,
          onGrant: (g) => generationGrants.push(g),
        });
        if (smiles.length === 0) {
          // Genuinely benign: the job succeeded but produced no usable SMILES.
          return { skipped: true, reason: 'Generator returned no usable SMILES' };
        }
      }

      if (onStatus) onStatus('Fetching FM4M representations...');

      const chunkSize = Math.max(1, Math.floor(Number(featurize.chunkSize)) || 25);
      const returnMode = featurize.return_mode || 'file';
      const totalRequestedRep = smiles.length;
      const runs = [];
      let done = 0;
      // The row's featurization model first, then the fallbacks (contract 1.6). The builders
      // already mapped the generative aliases bart, mhg and smi-ted to mol-xl (1.2.2), so the
      // runner no longer remaps.
      const requestedModel = featurize.model || 'mol-xl';
      const repCandidates = Array.from(new Set([requestedModel, 'Mordred', 'MorganFingerprint']));
      // Part 1a (D19): every run names its row and how it featurized, so results group by
      // row, Refine restores the model, return mode, inline max, chunk size and category from
      // the run (1.7), and ResultsTable can note a fallback (request.model !== requestedModel).
      const rowFields = {
        rowId: p.rowId ?? null,
        target: isRepresent ? requestedModel : (p.generator ?? null),
        customModelId: isRepresent ? null : (p.customModelId ?? null),
        kind: isRepresent ? 'represent' : 'generate',
        domain: p.domain ?? null,
      };
      const featurizeFields = {
        requestedModel,
        returnMode,
        ...(returnMode === 'inline' && Number.isFinite(featurize.max_items_inline) ? { maxItemsInline: featurize.max_items_inline } : {}),
        chunkSize,
      };
      // D3 durability fix (2026-08-20): each chunk now goes through
      // POST /jobs/representation + poll rather than a single synchronous
      // POST /representation fetch: a tab close mid-chunk used to lose that
      // chunk's candidate rows permanently even if compute finished
      // server-side (docs/DURABILITY-AUDIT-2026-08-20.md finding D3). The
      // job lands in the fm4mJobs Firestore collection (uid + spectra_run_id
      // scoped, see models/ibm-materials-service/persistence.py) the moment
      // it's submitted, before this chunk's compute even starts.
      for (let i = 0; i < smiles.length; i += chunkSize) {
        const part = smiles.slice(i, i + chunkSize);
        let lastErr = null;
        let success = false;
        for (const modelName of repCandidates) {
          try {
            const data = await submitAndPoll({
              base,
              submitPath: '/jobs/representation',
              body: fm4mFeaturizeBody({ ...featurize, return_mode: returnMode }, modelName, part),
              signal: options.signal,
              activeJobIds,
              onStatus,
              statusLabel: 'FM4M: featurizing',
              authHeaders: effAuthHeaders,
            });
            // `base` records where this chunk ran, so the pooling gate (spec 2026-10-07
            // Part 0.5) can keep BYOC and endpoint-mode custom-model results out of bulk
            // capture. Mirrors MattergenRunner, which tags every run the same way.
            runs.push({ ...rowFields, request: { inputs: part, model: modelName }, ...featurizeFields, response: data, base });
            success = true;
            break;
          } catch (e) {
            if (e?.name === 'AbortError') throw e;
            lastErr = e?.message ? new Error(`FM4M error: ${e.message} (model=${modelName})`) : e;
          }
        }
        if (!success) {
          throw lastErr || new Error('FM4M representation failed');
        }
        done += part.length;
        if (typeof options.onProgress === 'function') options.onProgress({ key: 'fm4m', done, total: totalRequestedRep, label });
      }
      // 0.3: the generation grant, summed across its jobs, rides this row's FIRST run
      // only, so summing `granted` over any list of runs never counts the row twice.
      const generationGrant = sumGrants(generationGrants);
      if (runs.length > 0 && generationGrant.granted !== undefined) runs[0] = { ...runs[0], ...generationGrant };
      return { ok: true, total_requested: totalRequestedRep, runs };
    },

    /** Cancel all active FM4M jobs on the backend. */
    cancelAll() {
      for (const j of activeJobIds.current) {
        authedFetch(`${j.base}/jobs/${j.id}`, { method: 'DELETE', headers: j.authHeaders }).catch(() => {});
      }
      activeJobIds.current = [];
    },
  }));
  return null;
});

export default FM4MRunner;
